<template>
  <MainLayout>
    <!-- Header -->
    <PageHeader 
      icon="ri-notification-3-line"
      title="Notificaciones"
      subtitle="Mantente informado de las últimas novedades"
      :show-button="true"
      button-text="Actualizar"
      button-icon="ri-loop-right-line"
      @action="actualizar"
    />

    <div class="content-container">
      <!-- ============================================
           SECCIÓN DE NOTIFICACIONES
           ============================================ -->
      
      <!-- Estado de carga notificaciones -->
      <div v-if="loadingNotificaciones" class="loading-state">
        <div class="spinner"></div>
        <p>Cargando notificaciones...</p>
      </div>

      <!-- Notificaciones -->
      <div v-else-if="notificaciones && notificaciones.length > 0" class="notifications-section">
        <div class="section-header">
          <h2 class="section-title">
            <i class="ri-message-3-line"></i>
            Mis Notificaciones
          </h2>
          <div class="section-actions">
            <button 
              v-if="notificacionesNoLeidas > 0"
              @click="marcarTodasLeidas"
              class="btn-mark-all"
              title="Marcar todas como leídas"
            >
              <i class="ri-check-double-line"></i>
              <span>Marcar todas como leídas</span>
            </button>
            <span class="notification-count">{{ notificaciones.length }} {{ notificaciones.length === 1 ? 'notificación' : 'notificaciones' }}</span>
          </div>
        </div>

        <div class="notifications-list">
          <div 
            v-for="notificacion in notificaciones" 
            :key="notificacion.id" 
            class="notification-item"
            :class="[
              { 'unread': notificacion.leida === 0 },
              { 'clickable': esNotificacionClickeable(notificacion.tipo) },
              `type-${getNotificacionTipo(notificacion.tipo)}`
            ]"
            @click="handleNotificacionClick(notificacion)"
          >
            <div class="notification-icon-wrapper">
              <div class="notification-icon" :class="`icon-${getNotificacionTipo(notificacion.tipo)}`">
                <i :class="getNotificacionIcono(notificacion.tipo)"></i>
              </div>
            </div>

            <div class="notification-body">
              <div class="notification-header">
                <h3 class="notification-title">{{ notificacion.titulo }}</h3>
                <span class="notification-date">{{ formatearFecha(notificacion.created_at) }}</span>
              </div>
              
              <p class="notification-message">{{ notificacion.mensaje }}</p>

              <div class="notification-meta">
                <span class="notification-type-badge" :class="`badge-${getNotificacionTipo(notificacion.tipo)}`">
                  {{ formatearTipo(notificacion.tipo) }}
                </span>
                <span v-if="notificacion.leida === 0" class="unread-indicator">
                  <i class="ri-checkbox-blank-circle-fill"></i>
                  No leída
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Botón cargar más -->
        <div v-if="hasMoreNotificaciones" class="load-more-container">
          <button 
            class="btn-load-more" 
            @click="cargarMasNotificaciones"
            :disabled="loadingMore"
          >
            <i v-if="!loadingMore" class="ri-arrow-down-line"></i>
            <div v-else class="spinner-small"></div>
            <span>{{ loadingMore ? 'Cargando...' : 'Cargar más notificaciones' }}</span>
          </button>
        </div>
      </div>

      <!-- Estado vacío notificaciones -->
      <div v-else class="empty-state">
        <i class="ri-notification-off-line"></i>
        <h3>No hay notificaciones</h3>
        <p>Cuando recibas notificaciones, aparecerán aquí.</p>
      </div>

      <!-- ============================================
           SECCIÓN DE BANNERS/PUBLICIDAD
           ============================================ -->
      <div v-if="banners && banners.length > 0" class="banners-section">
        <div class="section-header">
          <h2 class="section-title">
            <i class="ri-megaphone-line"></i>
            Anuncios
          </h2>
        </div>

        <div class="banners-grid">
          <div 
            v-for="(banner, index) in banners" 
            :key="`banner-${index}`" 
            class="banner-card"
            :class="{ 'has-link': banner.destination_url }"
          >
            <div class="card-image-wrapper">
              <a 
                v-if="banner.destination_url" 
                :href="banner.destination_url" 
                target="_blank"
                rel="noopener noreferrer"
                class="image-link"
              >
                <img 
                  :src="banner.url" 
                  :alt="`Anuncio ${index + 1}`"
                  class="banner-image"
                  @error="handleImageError"
                >
                <div class="image-overlay">
                  <i class="ri-external-link-line"></i>
                  <span>Ver más</span>
                </div>
              </a>
              <img 
                v-else
                :src="banner.url" 
                :alt="`Anuncio ${index + 1}`"
                class="banner-image"
                @error="handleImageError"
              >
            </div>

            <div v-if="banner.destination_url" class="card-footer">
              <a 
                :href="banner.destination_url" 
                target="_blank"
                rel="noopener noreferrer"
                class="info-link"
              >
                <i class="ri-information-line"></i>
                <span>Más información</span>
                <i class="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import MainLayout from '../Layout/MainLayout.vue';
import PageHeader from '../Layout/PageHeader.vue';

export default {
  name: 'banners',
  components: {
    MainLayout,
    PageHeader
  },
  data() {
    return {
      loadingMore: false
    }
  },
  mounted() {
    this.actualizar();
  },
  methods: {
    ...mapActions({
      fetchBanners: 'banner/fetchBanners',
      fetchNotificaciones: 'notificaciones/fetchNotificaciones',
      loadMoreNotificaciones: 'notificaciones/loadMoreNotificaciones',
      marcarComoLeida: 'notificaciones/marcarComoLeida',
      marcarTodasComoLeidas: 'notificaciones/marcarTodasComoLeidas'
    }),
    actualizar() {
      // Cargar notificaciones y banners
      this.fetchNotificaciones({ page: 1, perPage: 10 });
      this.fetchBanners();
    },
    async cargarMasNotificaciones() {
      this.loadingMore = true;
      try {
        await this.loadMoreNotificaciones();
      } finally {
        this.loadingMore = false;
      }
    },
    async marcarTodasLeidas() {
      try {
        await this.marcarTodasComoLeidas();
        this.$toast?.success?.('Todas las notificaciones han sido marcadas como leídas');
      } catch (error) {
        console.error('Error al marcar todas las notificaciones:', error);
        this.$toast?.error?.('Error al marcar las notificaciones como leídas');
      }
    },
    handleImageError(event) {
      event.target.src = '/img/placeholder-notification.png';
    },
    esNotificacionClickeable(tipo) {
      return tipo === 'clase_agendada' || tipo === 'clase_cancelada';
    },
    async handleNotificacionClick(notificacion) {
      // Marcar como leída si no lo está
      if (notificacion.leida === 0) {
        try {
          await this.marcarComoLeida(notificacion.id);
        } catch (error) {
          console.error('Error al marcar notificación como leída:', error);
        }
      }

      // Redirigir si es una notificación clickeable
      if (this.esNotificacionClickeable(notificacion.tipo)) {
        this.$router.push({ name: 'mis_clases' });
      }
    },
    getNotificacionIcono(tipo) {
      const iconos = {
        'clase_agendada': 'ri-calendar-check-line',
        'clase_cancelada': 'ri-calendar-close-line',
        'evaluacion': 'ri-file-list-line',
        'tutoria': 'ri-user-star-line',
        'mensaje': 'ri-message-3-line',
        'aviso': 'ri-notification-3-line',
        'default': 'ri-information-line'
      };
      return iconos[tipo] || iconos.default;
    },
    getNotificacionTipo(tipo) {
      const tipos = {
        'clase_agendada': 'success',
        'clase_cancelada': 'danger',
        'evaluacion': 'info',
        'tutoria': 'info',
        'mensaje': 'default',
        'aviso': 'warning',
        'default': 'default'
      };
      return tipos[tipo] || tipos.default;
    },
    formatearTipo(tipo) {
      const nombres = {
        'clase_agendada': 'Clase Agendada',
        'clase_cancelada': 'Clase Cancelada',
        'evaluacion': 'Evaluación',
        'tutoria': 'Tutoría',
        'mensaje': 'Mensaje',
        'aviso': 'Aviso',
        'default': 'Notificación'
      };
      return nombres[tipo] || nombres.default;
    },
    formatearFecha(fecha) {
      if (!fecha) return '';
      try {
        const date = new Date(fecha);
        const ahora = new Date();
        const diffMs = ahora - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHoras = Math.floor(diffMs / 3600000);
        const diffDias = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'Hace un momento';
        if (diffMins < 60) return `Hace ${diffMins} ${diffMins === 1 ? 'minuto' : 'minutos'}`;
        if (diffHoras < 24) return `Hace ${diffHoras} ${diffHoras === 1 ? 'hora' : 'horas'}`;
        if (diffDias < 7) return `Hace ${diffDias} ${diffDias === 1 ? 'día' : 'días'}`;
        
        return date.toLocaleDateString('es-CO', { 
          day: '2-digit', 
          month: 'long', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (e) {
        return '';
      }
    }
  },
  computed: {
    ...mapState({
      banners: state => state.banner.banners,
      notificaciones: state => state.notificaciones.notificaciones,
      loadingNotificaciones: state => state.notificaciones.loading,
      paginacion: state => state.notificaciones.paginacion
    }),
    ...mapGetters('notificaciones', [
      'hasMoreNotificaciones'
    ]),
    notificacionesNoLeidas() {
      if (!this.notificaciones) return 0;
      return this.notificaciones.filter(n => n.leida === 0).length;
    }
  }
}
</script>

<style scoped>
/* ============================================================================
   ESTADO DE CARGA
   ============================================================================ */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  min-height: 400px;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--theme-border-color);
  border-top-color: var(--app-primary-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 1.5rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-state p {
  color: var(--theme-text-secondary);
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
}

/* ============================================================================
   ESTADO VACÍO
   ============================================================================ */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  min-height: 400px;
  text-align: center;
  background: var(--theme-bg-card);
  border: 2px dashed var(--theme-border-color);
  border-radius: 16px;
  animation: fadeIn 0.5s ease-out;
}

.empty-state i {
  font-size: 5rem;
  color: var(--theme-text-muted);
  opacity: 0.5;
  margin-bottom: 1.5rem;
}

.empty-state h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--theme-text-primary);
  margin: 0 0 0.75rem 0;
}

.empty-state p {
  font-size: 1rem;
  color: var(--theme-text-secondary);
  max-width: 400px;
  margin: 0;
  line-height: 1.6;
}

/* ============================================================================
   ENCABEZADOS DE SECCIÓN
   ============================================================================ */
.notifications-section,
.banners-section {
  animation: fadeIn 0.6s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--theme-border-color);
  gap: 1rem;
  flex-wrap: wrap;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--theme-text-primary);
  margin: 0;
}

.section-title i {
  color: var(--app-primary-blue);
  font-size: 1.75rem;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.btn-mark-all {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: linear-gradient(135deg, rgba(13, 78, 140, 0.1) 0%, rgba(79, 172, 254, 0.1) 100%);
  border: 1px solid rgba(13, 78, 140, 0.3);
  border-radius: 10px;
  color: var(--app-primary-blue);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-mark-all:hover {
  background: linear-gradient(135deg, rgba(13, 78, 140, 0.2) 0%, rgba(79, 172, 254, 0.2) 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(13, 78, 140, 0.2);
}

.btn-mark-all i {
  font-size: 1rem;
}

.notification-count {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--theme-text-secondary);
  padding: 0.5rem 1rem;
  background: var(--theme-bg-tertiary);
  border-radius: 20px;
}

/* ============================================================================
   LISTA DE NOTIFICACIONES
   ============================================================================ */
.notifications-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.notification-item {
  display: flex;
  gap: 1.25rem;
  padding: 1.5rem;
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border-color);
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--theme-shadow-sm);
  position: relative;
  overflow: hidden;
}

.notification-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--theme-border-color);
  transition: all 0.3s ease;
}

.notification-item.unread {
  background: linear-gradient(90deg, rgba(13, 78, 140, 0.05) 0%, var(--theme-bg-card) 100%);
  border-left-width: 4px;
}

.notification-item.unread::before {
  background: var(--app-primary-blue);
  width: 4px;
}

.notification-item:hover {
  transform: translateX(4px);
  box-shadow: var(--theme-shadow-md);
  border-color: var(--app-primary-blue);
}

.notification-item.clickable {
  cursor: pointer;
}

.notification-item.clickable:hover {
  transform: translateX(6px);
  box-shadow: var(--theme-shadow-lg);
}

/* Iconos de notificación */
.notification-icon-wrapper {
  flex-shrink: 0;
}

.notification-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: all 0.3s ease;
}

.notification-item:hover .notification-icon {
  transform: scale(1.1) rotate(5deg);
}

.notification-icon.icon-success {
  background: linear-gradient(135deg, rgba(76, 217, 100, 0.2) 0%, rgba(93, 229, 120, 0.2) 100%);
  color: var(--theme-color-success);
}

.notification-icon.icon-danger {
  background: linear-gradient(135deg, rgba(255, 59, 48, 0.2) 0%, rgba(255, 107, 107, 0.2) 100%);
  color: var(--theme-color-danger);
}

.notification-icon.icon-warning {
  background: linear-gradient(135deg, rgba(255, 149, 0, 0.2) 0%, rgba(255, 186, 73, 0.2) 100%);
  color: var(--theme-color-warning);
}

.notification-icon.icon-info {
  background: linear-gradient(135deg, rgba(13, 78, 140, 0.2) 0%, rgba(79, 172, 254, 0.2) 100%);
  color: var(--app-primary-blue);
}

.notification-icon.icon-default {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  color: var(--theme-text-secondary);
}

/* Cuerpo de notificación */
.notification-body {
  flex: 1;
  min-width: 0;
}

.notification-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.notification-title {
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--theme-text-primary);
  margin: 0;
  line-height: 1.4;
}

.notification-date {
  font-size: 0.8125rem;
  color: var(--theme-text-muted);
  white-space: nowrap;
  flex-shrink: 0;
}

.notification-message {
  font-size: 0.9375rem;
  color: var(--theme-text-secondary);
  line-height: 1.6;
  margin: 0 0 1rem 0;
}

.notification-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.notification-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-success {
  background: rgba(76, 217, 100, 0.15);
  color: var(--theme-color-success);
}

.badge-danger {
  background: rgba(255, 59, 48, 0.15);
  color: var(--theme-color-danger);
}

.badge-warning {
  background: rgba(255, 149, 0, 0.15);
  color: var(--theme-color-warning);
}

.badge-info {
  background: rgba(13, 78, 140, 0.15);
  color: var(--app-primary-blue);
}

.badge-default {
  background: rgba(255, 255, 255, 0.1);
  color: var(--theme-text-secondary);
}

.unread-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--app-primary-blue);
  font-weight: 600;
}

.unread-indicator i {
  font-size: 0.5rem;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* ============================================================================
   BOTÓN CARGAR MÁS
   ============================================================================ */
.load-more-container {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}

.btn-load-more {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 2rem;
  background: var(--theme-bg-card);
  border: 2px solid var(--theme-border-color);
  border-radius: 12px;
  color: var(--theme-text-primary);
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: var(--app-font-family);
}

.btn-load-more:hover:not(:disabled) {
  background: var(--theme-hover-bg);
  border-color: var(--app-primary-blue);
  transform: translateY(-2px);
  box-shadow: var(--theme-shadow-md);
}

.btn-load-more:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-small {
  width: 18px;
  height: 18px;
  border: 2px solid var(--theme-border-color);
  border-top-color: var(--app-primary-blue);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* ============================================================================
   SECCIÓN DE BANNERS
   ============================================================================ */
.banners-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
}

/* ============================================================================
   TARJETA DE BANNER
   ============================================================================ */
.banner-card {
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border-color);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--theme-shadow-sm);
  display: flex;
  flex-direction: column;
  animation: slideUp 0.5s ease-out backwards;
}

.banner-card:nth-child(1) { animation-delay: 0.05s; }
.banner-card:nth-child(2) { animation-delay: 0.1s; }
.banner-card:nth-child(3) { animation-delay: 0.15s; }

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.banner-card.has-link {
  cursor: pointer;
}

.banner-card.has-link:hover {
  transform: translateY(-6px);
  box-shadow: var(--theme-shadow-lg);
  border-color: var(--app-primary-blue);
}

/* ============================================================================
   IMAGEN DE BANNER
   ============================================================================ */
.card-image-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: var(--theme-bg-secondary);
}

.image-link {
  display: block;
  position: relative;
  text-decoration: none;
}

.banner-image {
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.3s ease;
}

.banner-card.has-link:hover .banner-image {
  transform: scale(1.05);
}

/* Overlay al hacer hover */
.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, rgba(13, 78, 140, 0.7) 0%, rgba(13, 78, 140, 0.9) 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.banner-card.has-link:hover .image-overlay {
  opacity: 1;
}

.image-overlay i {
  font-size: 2.5rem;
  color: white;
  animation: bounce 1s ease-in-out infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

.image-overlay span {
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

/* ============================================================================
   FOOTER DE LA TARJETA
   ============================================================================ */
.card-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--theme-border-color);
  background: var(--theme-bg-tertiary);
}

.info-link {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: var(--app-primary-blue);
  text-decoration: none;
  font-size: 0.9375rem;
  font-weight: 600;
  transition: all 0.3s ease;
  padding: 0.5rem;
  border-radius: 8px;
}

.info-link:hover {
  background: rgba(13, 78, 140, 0.1);
  color: var(--app-primary-blue);
  text-decoration: none;
  gap: 0.75rem;
}

.info-link i:first-child {
  font-size: 1.125rem;
}

.info-link i:last-child {
  font-size: 1rem;
  transition: transform 0.3s ease;
}

.info-link:hover i:last-child {
  transform: translateX(4px);
}

/* ============================================================================
   RESPONSIVE
   ============================================================================ */
@media (max-width: 1024px) {
  .banners-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.5rem;
  }
}

@media (max-width: 768px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .section-actions {
    width: 100%;
    justify-content: space-between;
  }

  .btn-mark-all span {
    display: none;
  }

  .btn-mark-all {
    padding: 0.625rem;
  }

  .notification-count {
    align-self: flex-end;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .section-title i {
    font-size: 1.5rem;
  }

  .notification-item {
    flex-direction: column;
    gap: 1rem;
    padding: 1.25rem;
  }

  .notification-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  .notification-header {
    flex-direction: column;
    gap: 0.5rem;
  }

  .notification-date {
    align-self: flex-start;
  }

  .banners-grid {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }

  .empty-state {
    padding: 3rem 1.5rem;
    min-height: 300px;
  }

  .empty-state i {
    font-size: 4rem;
  }

  .empty-state h3 {
    font-size: 1.25rem;
  }

  .empty-state p {
    font-size: 0.9375rem;
  }

  .loading-state {
    padding: 3rem 1.5rem;
    min-height: 300px;
  }

  .spinner {
    width: 40px;
    height: 40px;
  }
}

@media (max-width: 480px) {
  .card-footer {
    padding: 0.875rem 1rem;
  }

  .info-link {
    font-size: 0.875rem;
  }

  .image-overlay i {
    font-size: 2rem;
  }

  .image-overlay span {
    font-size: 1rem;
  }

  .notification-title {
    font-size: 1rem;
  }

  .notification-message {
    font-size: 0.875rem;
  }

  .btn-load-more {
    width: 100%;
  }
}
</style>
